/**
 * Images for mail: upload them, describe them, drop them into a message.
 *
 * ## Why these are uploaded rather than inlined
 *
 * The instinct that produced the rest of this console's asset handling is to base64 the bytes
 * straight into `<img src="data:...">`. That is right for a preview and wrong for a message that
 * leaves the building: Gmail, Outlook.com and Yahoo all refuse a `data:` source, and every byte
 * counts against the ~102 kB clip threshold, so an inlined photo both fails to render and
 * truncates the footer it sits above. So the bytes go to the API, which serves them from a
 * public URL a mail client can fetch without a session, and the message carries a link.
 *
 * ## Why alt text is a required field and not an optional one
 *
 * Most clients block images by default. For a large share of recipients the alt text *is* the
 * image. An operator made to type one sentence writes something useful; one who is not leaves a
 * blank rectangle in every inbox. The API refuses an empty one, and so does this form.
 *
 * ## Why an image can exist with no URL
 *
 * `EmailImage.url` is nullable by contract: a deployment with no public address configured
 * stores the bytes but cannot reference them. That is a real state and the panel says so, rather
 * than offering a tag that resolves to nothing in every inbox.
 */
import { useRef, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { EmailImage } from '../contracts/types.js';
import {
  EMAIL_IMAGE_ACCEPT,
  EMAIL_IMAGE_LABEL_MAX,
  EMAIL_IMAGE_MAX_BYTES,
  EMAIL_IMAGE_TYPE_LABEL,
  checkAltText,
  checkImageFile,
  formatBytes,
  imageTagFor,
  readFileAsBase64,
  totalBytes,
} from '../lib/emailAttachments.js';
import { Badge, Button, Card, CopyButton, Field, Input } from './ui.js';
import { IconImage, IconX } from './icons.js';
import { useToast } from './Toast.js';

export function EmailImageLibrary({
  images,
  canWrite,
  onChange,
  onInsert,
  inUseIds = [],
  hint,
}: {
  images: readonly EmailImage[];
  canWrite: boolean;
  onChange: (next: readonly EmailImage[]) => void;
  /** Drop the `<img>` tag into the draft at the caller's chosen place. */
  onInsert?: (image: EmailImage, tag: string) => void;
  /**
   * Images the message on screen currently links to.
   *
   * Deleting one of these would leave a live `<img>` pointing at nothing, and a broken image in
   * an email is not a rendering glitch: recipients read it as a message that has been tampered
   * with. Removal is refused until the tag comes out of the message.
   */
  inUseIds?: readonly string[];
  hint?: string;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [pending, setPending] = useState<{ file: File; preview: string } | null>(null);
  const [altText, setAltText] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const used = totalBytes(images);
  const inUse = new Set(inUseIds);

  function choose(file: File | undefined): void {
    if (file === undefined) return;
    const verdict = checkImageFile(file);
    if (!verdict.ok) {
      toast.error('Cannot use that file', verdict.error);
      return;
    }
    if (pending !== null) URL.revokeObjectURL(pending.preview);
    setPending({ file, preview: URL.createObjectURL(file) });
    // The file name is a fair first guess at a label and a terrible one at a description, so it
    // seeds the label only. The description is the field the operator has to actually think about.
    setLabel(file.name.replace(/\.[a-z0-9]+$/i, '').slice(0, EMAIL_IMAGE_LABEL_MAX));
    setAltText('');
    setProblem(null);
  }

  function discard(): void {
    if (pending !== null) URL.revokeObjectURL(pending.preview);
    setPending(null);
    setAltText('');
    setLabel('');
    setProblem(null);
  }

  async function upload(): Promise<void> {
    if (pending === null || busy) return;
    const alt = checkAltText(altText);
    if (!alt.ok) {
      setProblem(alt.error);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const dataBase64 = await readFileAsBase64(pending.file);
      const image = await api.uploadEmailImage({
        dataBase64,
        label: label.trim(),
        altText: altText.trim(),
      });
      onChange([...images, image]);
      discard();
      toast.success('Image added', `${formatBytes(image.byteSize)} and ready to use.`);
    } catch (err) {
      setProblem(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(image: EmailImage): Promise<void> {
    if (removing !== null || inUse.has(image.id)) return;
    setRemoving(image.id);
    try {
      await api.deleteEmailImage(image.id);
      onChange(images.filter((i) => i.id !== image.id));
    } catch (err) {
      toast.error('Could not remove that image', messageOf(err));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <Card
      title="Images"
      actions={
        <span className="faint" style={{ fontSize: 12 }}>
          {images.length === 1 ? '1 image' : `${String(images.length)} images`} · {formatBytes(used)}
        </span>
      }
    >
      {hint !== undefined && (
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          {hint}
        </p>
      )}

      {pending === null ? (
        <div
          className="dropzone"
          role="button"
          tabIndex={0}
          aria-disabled={!canWrite}
          onClick={() => canWrite && fileRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) fileRef.current?.click();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (canWrite) choose(e.dataTransfer.files[0]);
          }}
        >
          <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
            <IconImage size={24} />
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
              Drop an image here, or click to browse
            </div>
            <div className="faint" style={{ fontSize: 12 }}>
              {EMAIL_IMAGE_TYPE_LABEL} · up to {formatBytes(EMAIL_IMAGE_MAX_BYTES)} each
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={EMAIL_IMAGE_ACCEPT}
            hidden
            disabled={!canWrite}
            onChange={(e) => {
              choose(e.target.files?.[0]);
              // Cleared, or choosing the same file again after a refusal fires no change event.
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <div className="mailupload">
          <img className="mailupload__preview" src={pending.preview} alt="" />
          <div className="mailupload__fields">
            <Field label="Describe the image" htmlFor="img-alt">
              <Input
                id="img-alt"
                value={altText}
                maxLength={200}
                placeholder="What somebody who cannot see it needs to know"
                onChange={(e) => setAltText(e.target.value)}
              />
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                Required. Most mail clients block images, so for a lot of readers this is the
                image.
              </div>
            </Field>
            <Field label="Name it, for your own list (optional)" htmlFor="img-label">
              <Input
                id="img-label"
                value={label}
                maxLength={EMAIL_IMAGE_LABEL_MAX}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Field>
            {problem !== null && (
              <div className="form-error" role="alert">
                {problem}
              </div>
            )}
            <div className="mailupload__actions">
              <span className="faint" style={{ fontSize: 12 }}>
                {pending.file.name} · {formatBytes(pending.file.size)}
              </span>
              <span className="tpl-actions__spacer" />
              <Button variant="ghost" size="sm" disabled={busy} onClick={discard}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" loading={busy} onClick={() => void upload()}>
                Upload
              </Button>
            </div>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <ul className="mailassets">
          {images.map((image) => {
            const tag = imageTagFor(image);
            return (
              <li className="mailasset" key={image.id}>
                {image.url === null ? (
                  <span className="mailasset__thumb mailasset__thumb--none" aria-hidden>
                    <IconImage size={18} />
                  </span>
                ) : (
                  <img className="mailasset__thumb" src={image.url} alt="" />
                )}
                <span className="mailasset__main">
                  <span className="mailasset__name">
                    {image.label.trim() === '' ? image.altText : image.label}
                  </span>
                  <span className="faint" style={{ fontSize: 12 }}>
                    {formatBytes(image.byteSize)} · {image.mime.replace('image/', '').toUpperCase()}
                    {image.width !== null && image.height !== null
                      ? ` · ${String(image.width)}x${String(image.height)}`
                      : ''}
                  </span>
                </span>
                <span className="mailasset__actions">
                  {tag === null ? (
                    <Badge tone="amber">No public address</Badge>
                  ) : (
                    <>
                      {onInsert !== undefined && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canWrite}
                          onClick={() => onInsert(image, tag)}
                        >
                          Insert
                        </Button>
                      )}
                      <CopyButton value={tag} label={`the image tag for ${image.altText}`} />
                    </>
                  )}
                  {inUse.has(image.id) && <Badge tone="violet">In use</Badge>}
                  <button
                    type="button"
                    className="footer-link__x"
                    aria-label={`Remove ${image.altText}`}
                    title={
                      inUse.has(image.id)
                        ? 'This message links to it. Take the image out of the message first.'
                        : `Remove ${image.altText}`
                    }
                    disabled={!canWrite || removing !== null || inUse.has(image.id)}
                    onClick={() => void remove(image)}
                  >
                    <IconX size={14} />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {images.some((image) => image.url === null) && (
        <div className="mailnote" role="status" style={{ marginTop: 12 }}>
          Some images have no public address, so they cannot be put in a message. That happens
          when the deployment has no public URL configured for serving them.
        </div>
      )}
    </Card>
  );
}
